/**
 * Biomes (decision 87: "switch biomes on now; adjust from play"; WORKBOARD §10.10, BUILDLIST 62.5).
 *
 * Mapgen's own biome is the planet type — picked by star and orbit, it owns terrain, strata, hazards,
 * ambience and the sky, and its flora and rocks (`eligibleFlora` / `eligibleRocks`) are the **classic
 * biome**: today's giant, planet-scaled trees and rocks, unchanged. A biome here is one level down:
 * a named set of trees and rocks of the **normal** size class (sized from the person), shared across
 * planet types, laid down in stands.
 *
 * Switched on where a person-scale battle is fought: `dressBattleSite` runs in match setup when the
 * scale profile shrinks people (k_human < 1) and dresses the battle's area — around and between the
 * objectives and the two sides — with stands. One to three biome regions per battle, each stand taking
 * the biome of the nearest region, so biomes mix on one planet. Classic matches (k = 1) are untouched.
 *
 * Bounded on purpose: a few hundred trunks near the battle, not a planet-wide carpet. The planet-wide
 * version is per-cell placement from a seed plus a destroyed set (PERFORMANCE_ARCHITECTURE §4.3 /
 * §4.9; BUILDLIST E5.3 / E5.4).
 *
 * Pure and deterministic: a seeded stream of its own (`<seed>-biomes`), dmath for every angle.
 */
import type { GameState, FloraState, RockState, Vec3, PlanetArchetype, PlanetState, BiomeId, FloraType, RockType } from '../types';
import { SeededRandom } from './random';
import { dsin, dcos, dacos } from './dmath';
import { normalize, cross, dot } from '../math';
import { datan2 } from './dmath';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { getSurfaceRadius } from './surface';
import { biomeDressing, isSimPiece, treesOn, type SimPiece } from './biomeDressing';
import { treeScale } from './treeScale';
import { groundMaterialAt } from './groundMaterial';
import { siteChoices, climateTrees } from './biomeClimate';
import { climateCellAt } from './climate';

export interface BiomeDef {
  id: BiomeId;
  name: string;
  /** trees and rocks, weighted */
  trees: Array<[FloraType, number]>;
  rocks: Array<[RockType, number]>;
  /** trees per stand, a stand's radius and the trees' spacing (person-heights) */
  stand: [number, number];
  radius: number;
  spacing: number;
  /** the planet types it grows on, weighted ('earth' = the earth palette) */
  planets: Partial<Record<PlanetArchetype | 'earth', number>>;
  /** Owner, Oct 10: sometimes a rock field — with this chance per region, `count` rocks of these kinds within `radius` people. */
  rockField?: { chance: number; kinds: Array<[RockType, number]>; count: [number, number]; radius: number };
  /** The rocks that sit on this biome's steep and high ground (none: its mountains are bare). */
  mountain?: Array<[RockType, number]>;
  /** Owner, Oct 10: the shapes its stands take, weighted (default: discs only). */
  shapes?: Array<[StandShape, number]>;
  /** Where its stands root: 'low' woods seek the valleys, 'high' stands the ridges, 'any' takes the ground as it comes (default). */
  ground?: 'low' | 'high' | 'any';
}

/**
 * The shape of a stand (owner, Oct 10: "shapes is interesting"). A **disc** is the wood as it was; a **belt** is a line
 * of trees — a hedgerow, a windbreak — cover to advance along; a **ring** leaves a clearing in its middle, a killing
 * ground; a **scatter** is sparse parkland; a **contour** is a belt laid along the slope's level line, the way trees
 * follow a hillside. Shapes change how a fight goes through the wood, not only how it looks.
 */
export type StandShape = 'disc' | 'belt' | 'ring' | 'scatter' | 'contour';

/**
 * Where a stand's trees go, in the stand's own plane: offsets in person-heights round the anchor, rejection-sampled
 * to `spacing`, at most `n` of them. `heading` turns the layout (a belt's or contour's direction; radians). Pure and
 * seeded; the sheet draws the same layouts on flat ground that the battle lays on the sphere.
 */
export function layoutStand(shape: StandShape, n: number, radius: number, spacing: number, rng: SeededRandom, heading = 0): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  const count = shape === 'scatter' ? Math.max(2, Math.round(n * 0.7)) : n;
  const gap = shape === 'scatter' ? spacing * 1.5 : shape === 'belt' || shape === 'contour' ? spacing * 0.9 : spacing;
  const ch = dcos(heading), sh = dsin(heading);
  for (let i = 0, tries = 0; i < count && tries < count * 40; tries++) {
    let x: number, y: number;
    if (shape === 'belt' || shape === 'contour') {
      // along the line, with the width falling off toward the edges (two draws summed read as a soft band)
      const along = rng.range(-1.15, 1.15) * radius;
      const across = (rng.range(-1, 1) + rng.range(-1, 1)) * 0.5 * radius * 0.32;
      x = along * ch - across * sh; y = along * sh + across * ch;
    } else if (shape === 'ring') {
      const a = rng.range(0, Math.PI * 2), d = radius * Math.sqrt(rng.range(0.55 * 0.55, 1));
      x = dcos(a) * d; y = dsin(a) * d;
    } else {
      const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range(0, 1)) * radius * (shape === 'scatter' ? 1.5 : 1);
      x = dcos(a) * d; y = dsin(a) * d;
    }
    if (out.some(q => (q.x - x) * (q.x - x) + (q.y - y) * (q.y - y) < gap * gap)) continue;
    out.push({ x, y }); i++;
  }
  return out;
}

/** A stand's footprint radius for its shape, in the biome's radius units (how far from the anchor its trees reach). */
export function standReach(shape: StandShape): number {
  return shape === 'belt' || shape === 'contour' ? 1.2 : shape === 'scatter' ? 1.5 : 1;
}

export const DISC_ONLY: Array<[StandShape, number]> = [['disc', 1]];

/** Debug: the stands the last dressing laid (anchor, biome, shape, heading), for tests and the sheet. */
let lastPlan: Array<{ dir: Vec3; biome: BiomeId; shape: StandShape; heading: number; planetIndex: number }> = [];
export function lastStandPlan(): typeof lastPlan { return lastPlan; }

/** The biomes (design_sheets/biomes.html): mostly ordinary, the lantern grove the odd one. */
export const BIOMES: Record<BiomeId, BiomeDef> = {
  broadleaf: { id: 'broadleaf', name: 'broadleaf wood', trees: [['puffElm', 3], ['canopyOak', 1], ['paleBirch', 2]], rocks: [['erratic', 2], ['slab', 1]],
    stand: [10, 16], radius: 12, spacing: 3.4, planets: { thalia: 3, viridis: 3, pelago: 2, earth: 3 },
    shapes: [['disc', 4], ['belt', 2], ['ring', 1], ['contour', 1]], ground: 'low',
    rockField: { chance: 0.25, kinds: [['erratic', 3], ['cobble', 2]], count: [6, 10], radius: 10 }, mountain: [['tor', 3], ['erratic', 1]] },
  firStand: { id: 'firStand', name: 'fir stand', trees: [['stackFir', 4], ['spirePine', 1], ['snag', 1]], rocks: [['erratic', 2]],
    stand: [12, 20], radius: 12, spacing: 3, planets: { bruma: 3, thalia: 2, viridis: 2, earth: 2 },
    shapes: [['disc', 3], ['belt', 2], ['contour', 2], ['ring', 1]], ground: 'low',
    rockField: { chance: 0.35, kinds: [['erratic', 3], ['cobble', 3], ['shard', 1]], count: [8, 14], radius: 12 }, mountain: [['shard', 3], ['tor', 2]] },
  heath: { id: 'heath', name: 'windswept heath', trees: [['windPine', 3], ['snag', 1]], rocks: [['slab', 2], ['erratic', 1]],
    stand: [4, 7], radius: 14, spacing: 6, planets: { pallor: 3, bruma: 2, ochrus: 1 },
    shapes: [['scatter', 3], ['belt', 2], ['disc', 1]], ground: 'high',
    rockField: { chance: 0.5, kinds: [['slab', 3], ['cobble', 2], ['erratic', 1]], count: [8, 16], radius: 14 }, mountain: [['tor', 3], ['shard', 2], ['slab', 1]] },
  savanna: { id: 'savanna', name: 'dry savanna', trees: [['bottleTree', 3], ['frondPalm', 1], ['snag', 1]], rocks: [['hoodoo', 2], ['slab', 1]],
    stand: [3, 6], radius: 16, spacing: 8, planets: { ochrus: 3, pallor: 1, tephra: 1 },
    shapes: [['scatter', 4], ['disc', 1]], ground: 'any',
    rockField: { chance: 0.3, kinds: [['hoodoo', 2], ['slab', 2], ['cobble', 1]], count: [5, 9], radius: 14 }, mountain: [['hoodoo', 3], ['slab', 2]] },
  ashBarrens: { id: 'ashBarrens', name: 'ash barrens', trees: [['snag', 4], ['frondPalm', 1]], rocks: [['columns', 3]],
    stand: [6, 10], radius: 12, spacing: 4.5, planets: { tephra: 3, murex: 1 },
    shapes: [['scatter', 2], ['disc', 2], ['belt', 1]], ground: 'any',
    rockField: { chance: 0.6, kinds: [['columns', 3], ['shard', 2], ['cobble', 1]], count: [8, 16], radius: 12 }, mountain: [['columns', 3], ['shard', 2]] },
  lanternGrove: { id: 'lanternGrove', name: 'lantern grove', trees: [['lanternFir', 4], ['paleBirch', 1]], rocks: [['veined', 2], ['erratic', 1]],
    stand: [8, 12], radius: 11, spacing: 3.5, planets: { murex: 2, bruma: 1, pallor: 1 },
    shapes: [['ring', 2], ['disc', 2], ['contour', 1]], ground: 'low',
    rockField: { chance: 0.3, kinds: [['veined', 2], ['erratic', 2], ['cobble', 1]], count: [5, 9], radius: 10 }, mountain: [['shard', 2], ['veined', 1]] },
  // Owner, Oct 10: four more biomes from the same kit (no new models) — the stands differ in mix, light, shape and ground.
  birchGlade: { id: 'birchGlade', name: 'birch glade', trees: [['paleBirch', 4], ['puffElm', 1]], rocks: [['erratic', 2], ['cobble', 1]],
    stand: [8, 14], radius: 11, spacing: 3.2, planets: { thalia: 2, bruma: 2, viridis: 1, pelago: 1, earth: 2 },
    shapes: [['ring', 3], ['disc', 2], ['belt', 1]], ground: 'low',
    rockField: { chance: 0.2, kinds: [['erratic', 3], ['cobble', 2]], count: [5, 8], radius: 9 }, mountain: [['erratic', 2], ['tor', 1]] },
  burn: { id: 'burn', name: 'the burn', trees: [['snag', 6], ['stackFir', 1]], rocks: [['shard', 2], ['cobble', 2]],
    stand: [8, 14], radius: 12, spacing: 3, planets: { tephra: 2, thalia: 1, bruma: 1, viridis: 1, pallor: 1 },
    shapes: [['disc', 2], ['scatter', 2], ['belt', 1]], ground: 'any',
    rockField: { chance: 0.3, kinds: [['shard', 3], ['cobble', 2]], count: [6, 10], radius: 10 }, mountain: [['shard', 3], ['tor', 1]] },
  scrub: { id: 'scrub', name: 'dry scrub', trees: [['windPine', 2], ['bottleTree', 2], ['snag', 1]], rocks: [['cobble', 3], ['slab', 1]],
    stand: [5, 9], radius: 14, spacing: 5, planets: { ochrus: 3, pallor: 2, tephra: 1, sonora: 1 },
    shapes: [['scatter', 3], ['belt', 1], ['disc', 1]], ground: 'any',
    rockField: { chance: 0.4, kinds: [['cobble', 3], ['slab', 2], ['hoodoo', 1]], count: [6, 12], radius: 12 }, mountain: [['slab', 2], ['hoodoo', 2], ['tor', 1]] },
  hollowWood: { id: 'hollowWood', name: 'hollow wood', trees: [['lanternFir', 2], ['snag', 2], ['windPine', 1]], rocks: [['veined', 3], ['shard', 2]],
    stand: [7, 12], radius: 12, spacing: 3.6, planets: { sonora: 3, murex: 1 },
    shapes: [['ring', 2], ['disc', 1], ['contour', 1]], ground: 'low',
    rockField: { chance: 0.35, kinds: [['veined', 3], ['shard', 2], ['cobble', 1]], count: [5, 9], radius: 10 }, mountain: [['shard', 3], ['veined', 2]] },
};
export const BIOME_IDS = Object.keys(BIOMES) as BiomeId[];

/** The biomes a planet grows, weighted (none on a crystal world, a star, a moonlet without a type). */
export function biomesFor(planet: Pick<PlanetState, 'archetype' | 'palette'>): Array<[BiomeId, number]> {
  const key = planet.palette?.name === 'earth' ? 'earth' : planet.archetype as PlanetArchetype;
  return BIOME_IDS.map(id => [id, BIOMES[id].planets[key] ?? 0] as [BiomeId, number]).filter(([, w]) => w > 0);
}

export function pick<T>(list: Array<[T, number]>, rng: SeededRandom): T {
  const total = list.reduce((a, [, w]) => a + w, 0);
  let x = rng.range(0, total);
  for (const [v, w] of list) { x -= w; if (x <= 0) return v; }
  return list[list.length - 1][0];
}

/** A tangent basis at `d`: the stand's plane (t1, t2), the same one everywhere so headings mean the same thing. */
export function tangentBasis(d: Vec3): [Vec3, Vec3] {
  const ref = Math.abs(d.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const t1 = normalize(cross(d, ref)), t2 = normalize(cross(d, t1));
  return [t1, t2];
}

/** The direction `angle` radians from `c` along the plane direction (cp, sp) in its tangent basis. */
function stepFrom(c: Vec3, t1: Vec3, t2: Vec3, cp: number, sp: number, angle: number): Vec3 {
  const s = dsin(angle), k = dcos(angle);
  return normalize({ x: c.x * k + (t1.x * cp + t2.x * sp) * s, y: c.y * k + (t1.y * cp + t2.y * sp) * s, z: c.z * k + (t1.z * cp + t2.z * sp) * s });
}

/** A direction within `angle` of `c` (uniform over the cap). */
function inCap(c: Vec3, angle: number, rng: SeededRandom): Vec3 {
  const u = rng.range(dcos(angle), 1), th = dacos(u), ph = rng.range(0, Math.PI * 2);
  const [t1, t2] = tangentBasis(c);
  return stepFrom(c, t1, t2, dcos(ph), dsin(ph), th);
}
const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, dot(a, b))));

/** A point of a stand's plane (offsets in person-heights, `P` a person-height as an angle) on the sphere. */
export function offsetOnSphere(anchor: Vec3, t1: Vec3, t2: Vec3, x: number, y: number, P: number): Vec3 {
  const len = Math.sqrt(x * x + y * y);
  if (len < 1e-9) return anchor;
  return stepFrom(anchor, t1, t2, x / len, y / len, len * P);
}

/** The ground's gradient at `d` in its tangent basis (rise over run along t1 and t2), over a step of `step` world units. */
function gradientAt(planet: PlanetState, d: Vec3, step: number): { sx: number; sy: number; slope: number } {
  const [t1, t2] = tangentBasis(d);
  const a = step / planet.radius;
  const at = (t: Vec3, s: number) => getSurfaceRadius(planet, normalize({ x: d.x + t.x * a * s, y: d.y + t.y * a * s, z: d.z + t.z * a * s }));
  const sx = (at(t1, 1) - at(t1, -1)) / (2 * step), sy = (at(t2, 1) - at(t2, -1)) / (2 * step);
  return { sx, sy, slope: Math.sqrt(sx * sx + sy * sy) };
}

/** The ground's slope at `d` (rise over run) over a step of `step` (world units). */
export function slopeAt(planet: PlanetState, d: Vec3, step: number): number {
  return gradientAt(planet, d, step).slope;
}

/** The level line's heading at `d` (radians in the tangent basis): across the slope, not down it. Flat ground: 0. */
export function contourHeading(planet: PlanetState, d: Vec3, step: number): number {
  const g = gradientAt(planet, d, step);
  return g.slope < 1e-6 ? 0 : datan2(g.sy, g.sx) + Math.PI / 2;
}

/** A stand won't root where the ground is this steep at its anchor (its trees may still climb the slope to BIOME_MAX_SLOPE). */
export const STAND_ANCHOR_MAX_SLOPE = 0.5;
/** Anchor candidates drawn per stand when a biome seeks low or high ground. */
export const GROUND_PICKS = 3;

/** The steepest ground a tree or rock stands on (rise over run). */
export const BIOME_MAX_SLOPE = 0.8;
/** Mountain rocks sit where the ground is at least this steep, or within the top share of the area's heights. */
export const MOUNTAIN_SLOPE = 0.32;
export const MOUNTAIN_TOP_SHARE = 0.3;
/** Rock fields and mountain rocks per battle, at most. */
export const ROCK_FIELDS_MAX = 3;
/**
 * 74.4: where a sim piece lies from its stand tree (`from`, people) and how far it keeps from anything laid, a unit, an
 * objective or a building (`clear`, people): a log about six people long lies centred, its yaw its own (core/treeDraws
 * treeYaw), so it keeps its half length and a body's room from every trunk and rock whichever way it lies.
 */
const PIECE_PLACE: Record<SimPiece, { from: [number, number]; clear: number }> = {
  log: { from: [4, 6.5], clear: 3.8 }, mound: { from: [2.5, 4.5], clear: 1.6 }, shrub: { from: [1.5, 3], clear: 1.2 },
};
export const MOUNTAIN_ROCKS_MAX = 36;

/**
 * 67.11: at person scale the planet-scale rocks are gone. Mapgen's rocks are sized from the planet
 * (twenty to fifty people tall at k 1/20), and seen from the ground they read as flat brown wedges
 * with their base plane showing where the ground drops away; the rock fields and mountain tors (E5,
 * `size: 'normal'`) are the rocks at this scale. Pure; classic (k = 1) never calls it.
 */
export function dropGiantRocks(state: GameState): GameState {
  const rocks = state.rocks.filter(r => r.size === 'normal');
  return rocks.length === state.rocks.length ? state : { ...state, rocks };
}

/**
 * Dress a person-scale battle with biome stands (decision 87). No-op at the classic scale (k ≥ 1)
 * and on planets with no biome. Flora and rocks are appended with fresh ids; flora destruction
 * tracking, if the match keeps it, is re-counted.
 */
/** 67.12: the Lab's stand-density steps, × the stands a site would take (`normal` = today, bit-identical). */
export const STAND_DENSITY = { sparse: 0.5, normal: 1, dense: 1.6, thick: 2.4 } as const;
export type StandDensity = keyof typeof STAND_DENSITY;

/**
 * 67.46: biome patches (the owner, playing whole planets: the Lab's sites were rich, and "for whole planet it doesn't look
 * like we do that to the same degree"). One dressing runs at every size with the same caps (≤ 28 stands, ≤ 3 rock fields,
 * ≤ 36 outcrops, the scatter's 1,600 pieces), so on a board a thousand people across the stands lie 70–100× thinner than
 * in the Lab's site of about 70 — nothing is switched off, all of it is spread out. With `patches`, a site wider than
 * PATCH.minSpan gathers them where the fight goes instead: a patch at each objective, one at each side's base, and patches
 * along the front between the sides, each in a local valley (a biome of high ground takes the rise). Each patch has its own
 * biome (neighbours differ where the planet grows more than one), its stands, a rock field's chance and its share of the
 * mountain probes: the Lab's site, in pieces. The counts are lane F's to bound (67.45); off, the dressing is as it was.
 */
export const PATCH = {
  minSpan: 160,     // people: a site at least this wide (from its centre) is patched; the Lab's is ~70
  radius: 60,       // people: a patch's reach round its centre (stands keep a stand and 4 people apart: ~5 fit; the Lab's 70 holds ~12)
  objective: 4,     // stands in a patch at an objective (with its two by the objective, as without patches)
  front: 4,         // stands in a patch along the front
  base: 2,          // stands in a patch at a side's base
  step: 170,        // people between the front's patches (× 0.8–1.2, and set off across the front by up to a patch's reach)
  window: 60,       // people round each step where its valley is looked for
  probes: 9,        // candidates for the valley (the lowest; a high-ground biome's, the highest)
  fields: 6,        // rock fields at most (each patch's chance)
  outcrops: 48,     // mountain rocks at most (the probes shared among the patches)
  maxStands: 160,   // stands at most (67.46 step 3, lane F's curve, sweeps/2026-10-11-trees-at-scale.md: ~2,000 trees — the tick
                    // unchanged, a crater's rebuild ~1.5 ms, ~10 MB of tables; half the 5,000-tree budget, the rest for E5.4's far tier)
  /** §0.11 step B (3), the dressing per region of interest (`regions`): stands in a patch at a settlement, and the cap over a board — the regions' sum up to this (the 5,000-tree budget's other half goes to the forest's band, FOREST_HELD.maxTrees). */
  settlement: 3,
  maxStandsBoard: 320,
  /** Houses this close (people) are one settlement (core/settlements.ts lays a settlement's lots within ~30 people of its centre). */
  settlementPeople: 45,
};
export type PatchKind = 'objective' | 'base' | 'front' | 'settlement';
/** Debug: the patches the last dressing laid, for tests and the sheet. */
let lastPatches: Array<{ dir: Vec3; biome: BiomeId; kind: PatchKind; planetIndex: number }> = [];
export function lastPatchPlan(): typeof lastPatches { return lastPatches; }
/**
 * 67.46 item 1 (the owner's comparison: the Lab's site interleaved two or three biomes in one view; a patch holds one):
 * with `subRegions`, an objective's patch is two or three sub-regions — its own biome at its heart, one or two more of the
 * planet's own biomes (decision 151: its set, never another's) on a ring `reach` × PATCH.radius out — and each stand there
 * (the patch's, the two by the objective, a rock field, an outcrop) takes the nearest sub-region's biome, as the Lab's
 * site's regions do. A planet that grows one biome keeps one. Its own seeded stream; the counts and the cap unchanged.
 */
export const SUB_REGIONS = { count: [2, 3] as const, reach: 0.6 };
/**
 * 67.46 item 3 (the comparison: rocks round the fight are about half the Lab's): with `objectiveRocks`, every objective's
 * patch gets a rock field — its biome's field kinds and count, not a chance, within `reach` × PATCH.radius, clear of the
 * capture circle and (if it can) of the stands — on top of the patches' chance fields; and the mountain probes are laid
 * along the front instead of round the patches — `steps` along the line from base to base, at each `across` people either
 * side of it — so the outcrops take the high and steep ground in view of the front (a ridge over the fight gets its
 * tors). Its own seeded stream for the fields; off, the dressing is today's.
 */
export const OBJECTIVE_ROCKS = { reach: 0.8, steps: 27, across: [70, 140, 220] as const, keepOut: 4 };   // keepOut: people round an outcrop past the patches, kept from the planet's forest
let lastObjFields: Array<{ site: Vec3; biome: BiomeId; centre: boolean; rocks: number; tried: number }> = [];
/** Debug: each objective's rock field the last dressing laid (empty without `objectiveRocks`). */
export function lastObjectiveRockPlan(): typeof lastObjFields { return lastObjFields; }
let lastSubs: Array<{ dir: Vec3; biome: BiomeId; planetIndex: number }> = [];
/** Debug: the sub-regions the last dressing laid (empty without `subRegions`). */
export function lastSubRegionPlan(): typeof lastSubs { return lastSubs; }

/**
 * §0.11 step B (3), the dressing per region of interest (`opts.regions`; GameConfig.regionDressing): with a board, the
 * bases' patches stand at the board's bases (not at each side's people, who start forward in a push); every settlement
 * laid (core/settlements.ts: its houses, clustered within PATCH.settlementPeople) gets a patch of its own; the front's
 * patches run along `opts.routes` — the hook lane C's route graph fills (each a great-circle segment, base to base by
 * default: today's one front line) — and the cap over the regions' sum is PATCH.maxStandsBoard. Off: the dressing as
 * it was, byte for byte (the same draws in the same order).
 */
export function dressBattleSite(state: GameState, seed: string, density = 1, opts: { patches?: boolean; maxStands?: number; subRegions?: boolean; objectiveRocks?: boolean; regions?: boolean; routes?: ReadonlyArray<readonly Vec3[]> } = {}): GameState {
  const ps = personSetOf(state);
  if (ps.k >= 1) return state;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const rng = new SeededRandom(`${seed}-biomes`);
  const flora: FloraState[] = [...state.flora];
  const rocks: RockState[] = [...state.rocks];
  let floraId = flora.reduce((m, f) => Math.max(m, f.id), -1) + 1;
  let rockId = rocks.reduce((m, r) => Math.max(m, r.id), -1) + 1;
  const capture = state.config.modeConfig.captureRadius ?? ps.captureRadius;
  const planetsWithUnits = [...new Set(state.players.filter(p => p.alive).map(p => p.planetIndex))].sort((a, b) => a - b);
  // E5.4 step 2: where this dressing lays its stands (the planet's forest keeps out of them: core/forest.ts)
  const dressedAreas: NonNullable<GameState['dressedAreas']> = [];
  const standMargin = Math.max(...Object.values(BIOMES).map(b => b.radius)) + 4;   // a stand's radius and the gap, in people

  for (const pi of planetsWithUnits) {
    const planet = state.planets[pi];
    const choices0 = planet ? biomesFor(planet) : [];
    if (!planet || choices0.length === 0) continue;
    const R = planet.radius, P = person / R;   // a person-height as an angle on this planet
    const sites = (state.modeState.points?.sites ?? []).filter(s => s.planetIndex === pi).map(s => normalize(s.surfaceDir));
    const units = state.players.filter(p => p.planetIndex === pi && p.alive).map(p => normalize(p.surfaceDir));
    const structures = (state.structures ?? []).filter(s => s.planetIndex === pi).map(s => normalize(s.surfaceDir));
    const all = [...sites, ...units];
    let c = all.reduce((a, d) => ({ x: a.x + d.x, y: a.y + d.y, z: a.z + d.z }), { x: 0, y: 0, z: 0 });
    c = Math.sqrt(dot(c, c)) > 1e-9 ? normalize(c) : all[0];
    const area = Math.max(40 * P, all.reduce((m, d) => Math.max(m, arc(c, d)), 0) * 1.15);
    const capA = capture / R;
    const patched = !!opts.patches && area / P >= PATCH.minSpan;   // 67.46
    const heightOf = (d: Vec3) => getSurfaceRadius(planet, d);
    // 74.12 step 2 (core/biomeClimate.ts, behind biomeField): the site's biomes weighted by the climate cell at its centre — a cold
    // height's site stands in firs and heath, a hot lowland's in savanna and scrub; the same draws from the seed, other odds
    const byClimate = !!state.config.biomeField && !!planet.heightmap && planet.heightmap.length > 0;
    const choicesC = byClimate ? siteChoices(choices0, climateCellAt(planet, c)) : choices0;
    // 382: a forced biome (a preset's promise) heads the choices at the top weight, past the list and the table
    const forced = state.config.biomeForce;
    const choices: Array<[BiomeId, number]> = forced ? [[forced, Math.max(...choicesC.map(([, w]) => w))], ...choicesC.filter(([b]) => b !== forced)] : choicesC;

    // biome regions: one to three, each its own biome where the planet grows more than one
    const regionCount = Math.min(choices.length, rng.int(1, 3));
    const regions: Array<{ dir: Vec3; biome: BiomeId }> = [];
    const pool = choices.slice();
    for (let i = 0; i < regionCount; i++) {
      const biome = i === 0 && forced ? forced : pick(pool, rng);   // (382: the first region is the forced biome's)
      pool.splice(pool.findIndex(([b]) => b === biome), 1);
      regions.push({ dir: inCap(c, area, rng), biome });
      if (pool.length === 0) break;
    }
    // 67.46: the patches — at each objective, at each side's base, along the front between the sides in its valleys —
    // each its own biome; they are the regions then (a stand, a rock field, an outcrop takes the nearest patch's biome)
    const patches: Array<{ dir: Vec3; biome: BiomeId; kind: PatchKind; stands: number }> = [];
    const subsOf = new Map<number, Array<{ dir: Vec3; biome: BiomeId }>>();   // 67.46 item 1: an objective's patch's sub-regions
    let frontLine: [Vec3, Vec3] | null = null;   // 67.46 item 3: the front, from the first side's base to the last's
    if (patched) {
      const teams = [...new Set(state.players.filter(p => p.planetIndex === pi && p.alive).map(p => p.teamIndex))].sort((a, b) => a - b);
      const boardBases = opts.regions ? (state.board?.bases ?? []).filter(b => b.planetIndex === pi).sort((x, y) => x.team - y.team).map(b => normalize(b.dir)) : [];
      const bases = boardBases.length >= 2 ? boardBases : teams.map(t => {
        const ds = state.players.filter(p => p.planetIndex === pi && p.alive && p.teamIndex === t).map(p => normalize(p.surfaceDir));
        const sum = ds.reduce((a, d) => ({ x: a.x + d.x, y: a.y + d.y, z: a.z + d.z }), { x: 0, y: 0, z: 0 });
        return normalize(sum);
      });
      // the settlements laid here (opts.regions): their houses clustered — one patch at each cluster's centre
      const settlements: Vec3[] = [];
      if (opts.regions) {
        const houses = (state.structures ?? []).filter(s => s.planetIndex === pi && s.type === 'house' && s.health > 0).map(s => normalize(s.surfaceDir));
        const clusterA = (PATCH.settlementPeople * person) / R, taken = new Set<number>();
        houses.forEach((h, i) => {
          if (taken.has(i)) return;
          const members = [i];
          for (let j = i + 1; j < houses.length; j++) if (!taken.has(j) && members.some(m => arc(houses[m], houses[j]) < clusterA)) members.push(j);
          for (const m of members) taken.add(m);
          const sum = members.reduce((a, m) => ({ x: a.x + houses[m].x, y: a.y + houses[m].y, z: a.z + houses[m].z }), { x: 0, y: 0, z: 0 });
          settlements.push(normalize(sum));
        });
      }
      const biomeFor = (d: Vec3): BiomeId => {   // differs from the nearest patch's where the planet grows more than one
        const near = patches.reduce<{ b: BiomeId; a: number } | null>((m, q) => { const a = arc(d, q.dir); return !m || a < m.a ? { b: q.biome, a } : m; }, null);
        let b = pick(choices, rng);
        for (let i = 0; i < 4 && near && b === near.b && choices.length > 1; i++) b = pick(choices, rng);
        return b;
      };
      for (const [k, s] of sites.entries()) patches.push({ dir: s, biome: k === 0 && forced ? forced : biomeFor(s), kind: 'objective', stands: PATCH.objective });   // (382: the first objective's patch is the forced biome's)
      for (const b of bases) patches.push({ dir: b, biome: biomeFor(b), kind: 'base', stands: PATCH.base });
      for (const s of settlements) patches.push({ dir: s, biome: biomeFor(s), kind: 'settlement', stands: PATCH.settlement });
      // the front: from the first side's base to the last's, a patch every PATCH.step people, each in its window's valley
      // (§0.11 step B (3): along each route given — the hook for lane C's route graph; base to base, the one front, without)
      if (bases.length >= 2) frontLine = [bases[0], bases[bases.length - 1]];
      // (§0.11 step B (2): a route is a polyline — the route graph's vertices (core/planetRoutes.ts mainRoutes) — walked as one
      // length, a patch every PATCH.step along it; a two-point route is the great circle between its ends, today's one front)
      const routes: ReadonlyArray<readonly Vec3[]> = opts.routes ?? (bases.length >= 2 ? [[bases[0], bases[bases.length - 1]]] : []);
      for (const route of routes) {
        // the legs: each from one vertex toward the next (antipodal ends — a board of exactly ½ — have no one circle: the first tangent at the start, the same on every engine)
        const legs: Array<{ a: Vec3; t: Vec3; side: Vec3; len: number; from: number }> = [];
        let total = 0;
        for (let i = 1; i < route.length; i++) {
          const a = normalize(route[i - 1]), z = normalize(route[i]), len = arc(a, z);
          if (len < 1e-9) continue;
          const ct = cross(cross(a, z), a), t = Math.sqrt(dot(ct, ct)) > 1e-9 ? normalize(ct) : tangentBasis(a)[0];
          legs.push({ a, t, side: normalize(cross(a, t)), len, from: total });
          total += len;
        }
        if (!legs.length) continue;
        for (let along = PATCH.step * P * rng.range(0.8, 1.2); along < total - PATCH.step * P * 0.5; along += PATCH.step * P * rng.range(0.8, 1.2)) {
          let leg = legs[legs.length - 1];
          for (const l of legs) if (along < l.from + l.len) { leg = l; break; }
          const { a, t, side } = leg, s = along - leg.from;
          const onLine = normalize({ x: a.x * dcos(s) + t.x * dsin(s), y: a.y * dcos(s) + t.y * dsin(s), z: a.z * dcos(s) + t.z * dsin(s) });
          const off = rng.range(-1, 1) * PATCH.radius * P;   // not a string of beads: each set off across the front
          const mid = normalize({ x: onLine.x * dcos(off) + side.x * dsin(off), y: onLine.y * dcos(off) + side.y * dsin(off), z: onLine.z * dcos(off) + side.z * dsin(off) });
          if (patches.some(q => arc(mid, q.dir) < PATCH.radius * P * 1.6)) continue;   // an objective or a base already has one here
          const biome = biomeFor(mid), high = BIOMES[biome].ground === 'high';
          let best = mid, bestH = heightOf(mid);
          for (let i = 0; i < PATCH.probes; i++) {
            const d = inCap(mid, PATCH.window * P, rng), h = heightOf(d);
            if (slopeAt(planet, d, 2 * person) > STAND_ANCHOR_MAX_SLOPE) continue;
            if (high ? h > bestH : h < bestH) { best = d; bestH = h; }
          }
          patches.push({ dir: best, biome, kind: 'front', stands: PATCH.front });
        }
      }
      // 67.46 item 1: an objective's patch in two or three of the planet's biomes (SUB_REGIONS), from a stream of its own
      if (opts.subRegions && choices.length > 1) {
        const srng = new SeededRandom(`${seed}-subregions-${pi}`);
        patches.forEach((q, qi) => {
          if (q.kind !== 'objective') return;
          const own: Array<{ dir: Vec3; biome: BiomeId }> = [{ dir: q.dir, biome: q.biome }];
          const pool = choices.filter(([b]) => b !== q.biome), n = Math.min(choices.length, srng.int(SUB_REGIONS.count[0], SUB_REGIONS.count[1]));
          const [t1, t2] = tangentBasis(q.dir), turn = srng.range(0, 2 * Math.PI), r = SUB_REGIONS.reach * PATCH.radius;
          for (let i = 1; i < n && pool.length > 0; i++) {
            const biome = pick(pool, srng);
            pool.splice(pool.findIndex(([b]) => b === biome), 1);
            const a = turn + ((i - 1) * 2 * Math.PI) / Math.max(1, n - 1) + srng.range(-0.4, 0.4);
            own.push({ dir: offsetOnSphere(q.dir, t1, t2, dcos(a) * r, dsin(a) * r, P), biome });
          }
          subsOf.set(qi, own);
        });
      }
      regions.length = 0;
      patches.forEach((q, qi) => { for (const r of subsOf.get(qi) ?? [{ dir: q.dir, biome: q.biome }]) regions.push(r); });
      lastPatches = patches.map(q => ({ dir: q.dir, biome: q.biome, kind: q.kind, planetIndex: pi }));
      lastSubs = [...subsOf.values()].flat().map(r => ({ ...r, planetIndex: pi }));
    } else { lastPatches = []; lastSubs = []; }
    // (in people: a patch's reach and a stand's past it; unpatched, the area and a stand's past its rim)
    if (patched) for (const q of patches) dressedAreas.push({ planetIndex: pi, dir: q.dir, angle: (PATCH.radius + standMargin) * P });
    else {
      dressedAreas.push({ planetIndex: pi, dir: c, angle: area + standMargin * P });
      for (const st of sites) dressedAreas.push({ planetIndex: pi, dir: st, angle: capA * 3.2 + standMargin * P });   // its two stands by each objective
    }
    const biomeAt = (d: Vec3) => regions.reduce((best, r) => (arc(d, r.dir) < arc(d, best.dir) ? r : best)).biome;

    // stand anchors: two by every objective, then the rest of the area by its size
    const anchors: Array<{ dir: Vec3; biome: BiomeId }> = [];
    const clearOf = (d: Vec3, radius: number) =>
      sites.every(s => arc(d, s) > capA * 0.9 + radius) && units.every(u => arc(d, u) > 8 * P) && structures.every(s => arc(d, s) > 6 * P);
    const fits = (d: Vec3, biome: BiomeId) => {
      const r = BIOMES[biome].radius * P;
      return clearOf(d, r * 0.5) && anchors.every(a => arc(d, a.dir) > r + BIOMES[a.biome].radius * P + 4 * P);
    };
    const span = area / P;   // the area's radius in people
    const want = Math.round(density * Math.max(6, Math.min(28, Math.round((span * span) / 1800))));   // × the density (67.12); 1 = as it was
    // Placement with the terrain (owner, Oct 10): a biome that seeks low ground takes the lowest of a few candidates,
    // one that seeks high ground the highest; no stand roots on a cliff face. Height is read once per candidate.
    const groundPick = (draw: () => Vec3, biomeOf: (d: Vec3) => BiomeId): { d: Vec3; b: BiomeId } => {
      const first = draw(), b = biomeOf(first), pref = BIOMES[b].ground ?? 'any';
      if (pref === 'any') return { d: first, b };
      let best = first, bestH = heightOf(first);
      for (let i = 1; i < GROUND_PICKS; i++) {
        const d = draw(); if (biomeOf(d) !== b) continue;
        const h = heightOf(d);
        if (pref === 'low' ? h < bestH : h > bestH) { best = d; bestH = h; }
      }
      return { d: best, b };
    };
    const roots = (d: Vec3) => slopeAt(planet, d, 2 * person) <= STAND_ANCHOR_MAX_SLOPE;
    for (const s of sites) {
      for (let k = 0, tries = 0; k < 2 && tries < 40; tries++) {
        const { d, b } = groundPick(() => inCap(s, capA * 3.2, rng), biomeAt);
        if (arc(d, s) < capA * 1.6) continue;
        if (fits(d, b) && roots(d)) { anchors.push({ dir: d, biome: b }); k++; }
      }
    }
    if (patched) {
      // 67.46: each patch's stands within its reach, in its biome (× the density), to PATCH.maxStands in all
      // over the cap, every patch gives up its share (the far front as much as the objectives); the sheet's "same budget"
      // passes today's cap
      const cap = opts.maxStands ?? (opts.regions ? PATCH.maxStandsBoard : PATCH.maxStands), most = Math.round(cap * density) + sites.length * 2;
      const share = Math.min(1, cap / Math.max(1, patches.reduce((a, q) => a + q.stands, 0)));
      patches.forEach((q, qi) => {
        const n = Math.max(1, Math.round(q.stands * density * share)), subs = subsOf.get(qi);
        for (let k = 0, tries = 0; k < n && tries < n * 40 && anchors.length < most; tries++) {
          const d = inCap(q.dir, PATCH.radius * P, rng);
          // (67.46 item 1: in a patch of sub-regions, the nearest one's biome)
          const b = subs ? subs.reduce((m, r) => (arc(d, r.dir) < arc(d, m.dir) ? r : m)).biome : q.biome;
          if (fits(d, b) && roots(d)) { anchors.push({ dir: d, biome: b }); k++; }
        }
      });
    } else {
      for (let tries = 0; anchors.length < want + sites.length * 2 && tries < want * 40; tries++) {
        const { d, b } = groundPick(() => inCap(c, area, rng), biomeAt);
        if (fits(d, b) && roots(d)) anchors.push({ dir: d, biome: b });
      }
      // A rough world leaves few gentle anchors: rather than a bare battle, the rest take steeper ground (the trees
      // themselves still keep under BIOME_MAX_SLOPE), so the wood is about as thick as it was before the shapes.
      for (let tries = 0; anchors.length < want && tries < want * 20; tries++) {
        const d = inCap(c, area, rng), b = biomeAt(d);
        if (fits(d, b)) anchors.push({ dir: d, biome: b });
      }
    }

    // the stands: each in a shape the biome takes (a disc, a belt, a ring, a scatter, a contour along the slope), its
    // trees laid in the stand's plane and spaced by the biome, rocks at the rim, nothing on steep ground or on a unit
    const placed: Vec3[] = [];
    const standTrees: Array<{ dir: Vec3; biome: BiomeId }> = [];   // (74.4: the pieces lie beside them)
    lastPlan = [];
    for (const a of anchors) {
      const def = BIOMES[a.biome], r = def.radius * P, gap = def.spacing * P;
      const shape = pick(def.shapes ?? DISC_ONLY, rng);
      const heading = shape === 'contour' ? contourHeading(planet, a.dir, 2 * person) : shape === 'belt' ? rng.range(0, Math.PI) : 0;
      lastPlan.push({ dir: a.dir, biome: a.biome, shape, heading, planetIndex: pi });
      const [t1, t2] = tangentBasis(a.dir);
      const n = rng.int(def.stand[0], def.stand[1]);
      for (const o of layoutStand(shape, n, def.radius, def.spacing, rng, heading)) {
        const d = offsetOnSphere(a.dir, t1, t2, o.x, o.y, P);
        if (!clearOf(d, 0) || placed.some(q => arc(d, q) < gap * 0.85) || units.some(u => arc(d, u) < 3 * P)) continue;
        if (slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) continue;
        placed.push(d); standTrees.push({ dir: d, biome: a.biome });
        // (70.5: the biome's dressing — its trees on the ground's material where it names them; the draw as before)
        // (383: by the climate, the glowing kinds thinned outside a cold cell — a forced biome's stands keep their mix)
        const mix = treesOn(a.biome, groundMaterialAt(planet, d));
        const floraType = pick(byClimate && a.biome !== forced ? climateTrees(mix, climateCellAt(planet, d)) : mix, rng);   // (366: its species' scale range)
        flora.push({ id: floraId++, floraType, planetIndex: pi, surfaceDir: d, alive: true, scale: treeScale(floraType, rng), size: 'normal', biome: a.biome });
      }
      const reach = r * standReach(shape);
      const rocksHere = rng.int(1, 3);
      for (let i = 0, tries = 0; i < rocksHere && tries < 20; tries++) {
        const d = inCap(a.dir, reach * 1.2, rng);
        if (arc(d, a.dir) < reach * 0.5 || !clearOf(d, 0) || placed.some(q => arc(d, q) < gap * 0.8)) continue;
        if (slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) continue;
        placed.push(d); i++;
        rocks.push({ id: rockId++, rockType: pick(biomeDressing(a.biome).rocks.rim, rng), mode: 'medium', planetIndex: pi, surfaceDir: d, alive: true, scale: rng.range(0.8, 1.3), seed: rng.int(1, 1e6), size: 'normal', biome: a.biome });
      }
    }

    // 67.46 item 3: a rock field at every objective (OBJECTIVE_ROCKS), from a stream of its own, before the chance fields
    lastObjFields = [];
    if (patched && opts.objectiveRocks) {
      const orng = new SeededRandom(`${seed}-objrocks-${pi}`);
      for (const site of sites) {
        const biome = biomeAt(site), def = BIOMES[biome];
        if (!def.rockField) continue;
        const fr = def.rockField, reach = PATCH.radius * P * OBJECTIVE_ROCKS.reach, n = orng.int(fr.count[0], fr.count[1]), fieldR = fr.radius * P;
        // a centre on ground a rock sits on, clear of the stands if it can be (past 40 tries, of the capture circle only);
        // a field that lays under half its rocks there (a rough slope) tries again elsewhere, three centres at most
        let laid = 0, tried = 0, centres = 0;
        for (let attempt = 0; attempt < 3 && laid < Math.ceil(n / 2); attempt++) {
          let centre: Vec3 | null = null;
          for (let tries = 0; tries < 80 && !centre; tries++) {
            const d = inCap(site, reach, orng);
            if (clearOf(d, fr.radius * P) && slopeAt(planet, d, 2 * person) <= BIOME_MAX_SLOPE && (tries >= 40 || anchors.every(an => arc(d, an.dir) > (fr.radius + BIOMES[an.biome].radius) * P))) centre = d;
          }
          if (!centre) continue;
          centres++;
          for (let tries = 0; laid < n && tries < n * 20; tries++, tried++) {
            const d = inCap(centre, fieldR, orng);
            if (!clearOf(d, 0) || placed.some(q => arc(d, q) < 2.2 * P) || slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) continue;
            placed.push(d); laid++;
            rocks.push({ id: rockId++, rockType: pick(biomeDressing(biome).rocks.kinds, orng), mode: 'cluster', planetIndex: pi, surfaceDir: d, alive: true, scale: orng.range(0.7, 1.4), seed: orng.int(1, 1e6), size: 'normal', biome });
          }
        }
        lastObjFields.push({ site, biome, centre: centres > 0, rocks: laid, tried });
      }
    }
    // Owner, Oct 10: sometimes a rock field — per biome region, with the biome's chance, a cluster of its field
    // kinds on a patch clear of the stands (mode 'cluster' marks them). Appended after the stands, so they are as before.
    let fields = 0;
    for (const region of regions) {
      const def = BIOMES[region.biome];
      if (!def.rockField || fields >= (patched ? PATCH.fields : ROCK_FIELDS_MAX) || rng.range(0, 1) > def.rockField.chance) continue;
      let centre: Vec3 | null = null;
      for (let tries = 0; tries < 30 && !centre; tries++) {
        const d = inCap(region.dir, patched ? PATCH.radius * P * 1.2 : area * 0.8, rng);   // 67.46: in its patch
        if (clearOf(d, def.rockField.radius * P) && anchors.every(an => arc(d, an.dir) > (def.rockField!.radius + BIOMES[an.biome].radius) * P)) centre = d;
      }
      if (!centre) continue;
      fields++;
      const n = rng.int(def.rockField.count[0], def.rockField.count[1]), fieldR = def.rockField.radius * P;
      for (let i = 0, tries = 0; i < n && tries < n * 20; tries++) {
        const d = inCap(centre, fieldR, rng);
        if (!clearOf(d, 0) || placed.some(q => arc(d, q) < 2.2 * P) || slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) continue;
        placed.push(d); i++;
        rocks.push({ id: rockId++, rockType: pick(biomeDressing(region.biome).rocks.kinds, rng), mode: 'cluster', planetIndex: pi, surfaceDir: d, alive: true, scale: rng.range(0.7, 1.4), seed: rng.int(1, 1e6), size: 'normal', biome: region.biome });
      }
    }

    // Owner, Oct 10: rocks on the mountains — where the ground is steep or among the area's highest, the local
    // biome's mountain kinds (mode 'outcrop'). The area's heights are sampled first so "high" is relative to it.
    const heights: number[] = [];
    const probes: Vec3[] = [];
    if (patched && opts.objectiveRocks && frontLine) {
      // 67.46 item 3: along the front — its steps from base to base, at each `across` either side — the ground in view of it
      const [a, z] = frontLine, total = arc(a, z), t = normalize(cross(cross(a, z), a)), side = normalize(cross(a, t));
      for (let k = 0; k < OBJECTIVE_ROCKS.steps; k++) {
        const along = total * (k + 0.5) / OBJECTIVE_ROCKS.steps;
        const onLine = normalize({ x: a.x * dcos(along) + t.x * dsin(along), y: a.y * dcos(along) + t.y * dsin(along), z: a.z * dcos(along) + t.z * dsin(along) });
        for (const off of OBJECTIVE_ROCKS.across) for (const sgn of [-1, 1]) {
          const o = sgn * off * P;
          const d = inCap(normalize({ x: onLine.x * dcos(o) + side.x * dsin(o), y: onLine.y * dcos(o) + side.y * dsin(o), z: onLine.z * dcos(o) + side.z * dsin(o) }), 25 * P, rng);
          probes.push(d); heights.push(getSurfaceRadius(planet, d) - planet.radius);
        }
      }
    } else for (let i = 0; i < 160; i++) { const d = patched ? inCap(patches[i % patches.length].dir, PATCH.radius * P * 1.3, rng) : inCap(c, area, rng); probes.push(d); heights.push(getSurfaceRadius(planet, d) - planet.radius); }   // 67.46: shared among the patches
    const sorted = [...heights].sort((x, y) => x - y);
    const highAbove = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * (1 - MOUNTAIN_TOP_SHARE)))];
    let mountain = 0;
    for (let i = 0; i < probes.length && mountain < (patched ? PATCH.outcrops : MOUNTAIN_ROCKS_MAX); i++) {
      const d = probes[i], slope = slopeAt(planet, d, 2 * person);
      const steep = slope >= MOUNTAIN_SLOPE && slope <= BIOME_MAX_SLOPE, high = heights[i] >= highAbove && slope <= BIOME_MAX_SLOPE;
      if (!(steep || high) || !clearOf(d, 0) || placed.some(q => arc(d, q) < 3 * P)) continue;
      const kinds = BIOMES[biomeAt(d)].mountain;
      if (!kinds) continue;
      placed.push(d); mountain++;
      rocks.push({ id: rockId++, rockType: pick(kinds, rng), mode: 'outcrop', planetIndex: pi, surfaceDir: d, alive: true, scale: rng.range(0.9, 1.6), seed: rng.int(1, 1e6), size: 'normal', biome: biomeAt(d) });
      // (67.46 item 3: an outcrop out on the front's flanks, past the patches, keeps the planet's forest off it)
      if (patched && opts.objectiveRocks && !patches.some(q => arc(d, q.dir) < (PATCH.radius + standMargin) * P)) dressedAreas.push({ planetIndex: pi, dir: d, angle: OBJECTIVE_ROCKS.keepOut * P });
    }

    // 74.4 (DECISIONS 351): the dressing's pieces the sim holds — a fallen log, a termite mound (cover), a shrub
    // (concealment) — beside a stand tree at its biome's rate (core/biomeDressing.ts), from a stream of their own after
    // everything else, so the stands, the fields and the outcrops are laid as they were. The drawn pieces (fern, glowcap,
    // bones) are the stands' (rendering/treeStands.ts), never in state.
    const prng = new SeededRandom(`${seed}-pieces-${pi}`);
    for (const t of standTrees) {
      const [t1, t2] = tangentBasis(t.dir);
      for (const [piece, chance] of biomeDressing(t.biome).pieces) {
        if (!isSimPiece(piece) || prng.range(0, 1) >= chance) continue;
        const [lo, hi] = PIECE_PLACE[piece].from, clear = PIECE_PLACE[piece].clear * P;
        for (let tries = 0; tries < 6; tries++) {
          const ang = prng.range(0, 2 * Math.PI), dist = prng.range(lo, hi);
          const d = offsetOnSphere(t.dir, t1, t2, dcos(ang) * dist, dsin(ang) * dist, P);
          if (!clearOf(d, clear) || placed.some(q => arc(d, q) < clear) || slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) continue;
          placed.push(d);
          if (piece === 'mound') rocks.push({ id: rockId++, rockType: 'mound', mode: 'medium', planetIndex: pi, surfaceDir: d, alive: true, scale: prng.range(0.85, 1.15), seed: prng.int(1, 1e6), size: 'normal', biome: t.biome });
          else flora.push({ id: floraId++, floraType: piece, planetIndex: pi, surfaceDir: d, alive: true, scale: prng.range(0.85, 1.15), size: 'normal', biome: t.biome });
          break;
        }
      }
    }
  }
  return { ...state, flora, rocks, ...(dressedAreas.length ? { dressedAreas } : {}) };
}
